import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api, getUserId } from "../lib/api";
import { ArrowRight, ShieldCheck, Gauge, Files, MessagesSquare, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function LandingPage() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const existing = getUserId();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get("error");
    if (err) toast.error(`Sign-in issue: ${err.replace(/_/g, " ")}`);
  }, []);

  const connect = async () => {
    setLoading(true);
    try {
      const { auth_url } = await api.login();
      window.location.href = auth_url;
    } catch (e) {
      toast.error("Couldn't start Google sign-in.");
      setLoading(false);
    }
  };

  return (
    <Layout showNav={!!existing}>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 pb-16 pt-14 md:px-10 md:pb-28 md:pt-24">
          <div className="grid items-start gap-12 md:grid-cols-[1.05fr_0.95fr] md:gap-16">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600">
                <span className="h-1.5 w-1.5 rounded-full bg-[#0052FF]" />
                Account Intelligence for Google Ads
              </div>
              <h1 className="mt-6 font-heading text-4xl font-light tracking-tight text-slate-900 sm:text-5xl lg:text-[56px] lg:leading-[1.05]">
                Inherit any Google Ads account —
                <br />
                <span className="text-slate-500">understand it in 30 minutes.</span>
              </h1>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600">
                Handoff connects to the account, pulls the data, and writes a six-section briefing
                grounded in real numbers. What's working, what quietly got paused, and where spend is
                leaking — with sources on every finding.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                {existing ? (
                  <button
                    onClick={() => navigate("/new")}
                    className="group inline-flex items-center gap-2 rounded-md bg-[#0052FF] px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-[#0040CC]"
                    data-testid="cta-new-briefing"
                  >
                    Generate a new briefing
                    <ArrowRight size={16} strokeWidth={1.8} className="transition-transform group-hover:translate-x-0.5" />
                  </button>
                ) : (
                  <button
                    onClick={connect}
                    disabled={loading}
                    className="group inline-flex items-center gap-3 rounded-md bg-[#0052FF] px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-[#0040CC] disabled:opacity-60"
                    data-testid="connect-google-ads-btn"
                  >
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                      <GoogleMark />
                    </span>
                    {loading ? "Redirecting…" : "Connect Google Ads"}
                    <ArrowRight size={16} strokeWidth={1.8} className="transition-transform group-hover:translate-x-0.5" />
                  </button>
                )}
                <button
                  onClick={() => navigate("/briefings")}
                  className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50"
                  data-testid="view-saved-briefings-btn"
                >
                  View saved briefings
                </button>
              </div>
              <div className="mt-8 flex items-center gap-6 text-xs text-slate-500">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={14} strokeWidth={1.8} />
                  Read-only access
                </div>
                <div className="flex items-center gap-2">
                  <Gauge size={14} strokeWidth={1.8} />
                  Typical run ≈ 2-4 min
                </div>
              </div>
            </div>

            {/* Briefing preview card */}
            <div className="relative">
              <div className="card-soft relative overflow-hidden p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                      briefing preview
                    </p>
                    <p className="mt-1 font-heading text-lg font-medium text-slate-900">
                      Acme Corp — Search
                    </p>
                  </div>
                  <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                    complete
                  </span>
                </div>

                <div className="mt-5 grid grid-cols-3 gap-3">
                  <Stat label="Spend, 90d" value="$142,310" />
                  <Stat label="Conv." value="1,284" />
                  <Stat label="ROAS" value="3.2x" />
                </div>

                <div className="mt-5 space-y-2">
                  <PreviewRow num="01" title="Account Overview" color="text-slate-500" />
                  <PreviewRow num="02" title="What Is Working" color="text-emerald-700" />
                  <PreviewRow num="03" title="Worked Before, Missing Now" color="text-amber-700" />
                  <PreviewRow num="04" title="Active But Not Working" color="text-rose-700" />
                  <PreviewRow num="05" title="Structural Gaps" color="text-slate-500" />
                  <PreviewRow num="06" title="Flagged Anomalies" color="text-slate-500" />
                </div>
              </div>

              <div className="absolute -bottom-4 -right-4 hidden rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 shadow-md md:flex md:items-center md:gap-2">
                <Sparkles size={14} strokeWidth={1.8} className="text-[#0052FF]" />
                Written by Claude Sonnet 4.5
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-24">
          <div className="max-w-2xl">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">how it works</p>
            <h2 className="mt-3 font-heading text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl">
              Four steps from OAuth to insight.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              No throw-the-data-at-AI hacks. Numbers are computed server-side first — Claude only narrates
              what the data actually says.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <Step num="01" title="Connect" body="Google OAuth. Read-only scope. Refresh token stored encrypted." />
            <Step num="02" title="Pull" body="Batched GAQL queries. Retries with exponential backoff. Cached." />
            <Step num="03" title="Analyze" body="Backend computes rankings, anomalies, and gaps before anything is sent to Claude." />
            <Step num="04" title="Narrate" body="Claude writes six sections, each finding tied to a numeric source." />
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-24">
          <div className="grid gap-10 md:grid-cols-3">
            <div className="md:col-span-1">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">what you get</p>
              <h2 className="mt-3 font-heading text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl">
                A briefing that reads like your sharpest colleague wrote it.
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">
                Scannable, source-linked, and honest about gaps in the data. Share it read-only — no re-processing.
              </p>
            </div>
            <div className="grid gap-4 md:col-span-2 md:grid-cols-2">
              <FeatureCard
                icon={<Files size={18} strokeWidth={1.8} />}
                title="Six collapsible sections"
                body="Account Overview · What's Working · Worked Before, Missing Now · Active But Not Working · Structural Gaps · Flagged Anomalies."
              />
              <FeatureCard
                icon={<MessagesSquare size={18} strokeWidth={1.8} />}
                title="Ask Agent"
                body="Click any finding to chat with Claude grounded in the briefing context."
              />
              <FeatureCard
                icon={<ShieldCheck size={18} strokeWidth={1.8} />}
                title="Data limitation notes"
                body="Whenever search terms are thin or a query failed, it's disclosed inline — not hidden."
              />
              <FeatureCard
                icon={<Gauge size={18} strokeWidth={1.8} />}
                title="Snapshots + shareable URLs"
                body="Save any briefing. Share a read-only link with the next person on the account."
              />
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white py-10">
        <div className="mx-auto max-w-7xl px-6 text-sm text-slate-500 md:px-10">
          Handoff · Account Intelligence for Google Ads · Built for performance marketers.
        </div>
      </footer>
    </Layout>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.3-1.7 3.8-5.5 3.8-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.3 14.7 2.3 12 2.3 6.9 2.3 2.7 6.5 2.7 11.6S6.9 20.9 12 20.9c6.9 0 9.5-4.8 9.5-7.3 0-.5-.1-.9-.1-1.3H12z"/>
    </svg>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="num mt-0.5 text-lg font-semibold text-slate-900">{value}</p>
    </div>
  );
}

function PreviewRow({ num, title, color }) {
  return (
    <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
      <div className="flex items-center gap-3">
        <span className="num text-[10px] font-semibold tracking-wider text-slate-400">{num}</span>
        <span className="text-sm font-medium text-slate-700">{title}</span>
      </div>
      <span className={`text-xs font-medium ${color}`}>→</span>
    </div>
  );
}

function Step({ num, title, body }) {
  return (
    <div className="card-soft p-6">
      <p className="num text-xs font-semibold tracking-wider text-slate-400">{num}</p>
      <p className="mt-3 font-heading text-lg font-medium text-slate-900">{title}</p>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
    </div>
  );
}

function FeatureCard({ icon, title, body }) {
  return (
    <div className="card-soft p-5">
      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[#0052FF]/10 text-[#0052FF]">
        {icon}
      </div>
      <p className="mt-3 font-heading text-base font-medium text-slate-900">{title}</p>
      <p className="mt-1 text-sm leading-relaxed text-slate-600">{body}</p>
    </div>
  );
}
